import type { PushGeraet } from "@kundeninfo/api-client";
import type { BenachrichtigungsEinstellungenClient } from "./benachrichtigungenClient.js";

/**
 * PUSH AUF DIESEM GERÄT (Spec Tickets, Nachtrag, F105): Einschalten nur nach einer Handlung des
 * Nutzers (der Browser fragt sonst nicht oder sperrt die Seite), mit dem öffentlichen
 * VAPID-Schlüssel vom Server. Die Umgebung wird hereingereicht, damit die Logik ohne echten
 * Browser prüfbar ist.
 */

export type GeraeteZustand = "NICHT_UNTERSTUETZT" | "NICHT_ERLAUBT" | "AUS" | "AN";
type AboAusschnitt = { endpoint: string; toJSON: () => unknown; unsubscribe: () => Promise<boolean>;
  /** Der Schlüssel, mit dem das Abo angelegt wurde (fehlt in älteren Browsern). */
  options?: { applicationServerKey?: ArrayBuffer | null } };
export type PushUmgebung = {
  notification: { permission: NotificationPermission; requestPermission: () => Promise<NotificationPermission> } | undefined;
  /** `controller`: der Service Worker, der diese Seite steuert (`null` ohne — erster Besuch,
   *  harter Neuladen). Nur gelesen, um bei abgelaufener Frist zwischen AUS und NICHT_UNTERSTUETZT
   *  zu unterscheiden. */
  serviceWorker: { controller?: unknown; ready: Promise<{ pushManager: {
    getSubscription: () => Promise<AboAusschnitt | null>;
    subscribe: (o: { userVisibleOnly: boolean; applicationServerKey: Uint8Array }) => Promise<AboAusschnitt>;
  } }> } | undefined;
};

export function browserUmgebung(): PushUmgebung {
  const hatPush = typeof window !== "undefined" && "PushManager" in window && "serviceWorker" in navigator;
  return {
    notification: typeof Notification === "undefined" ? undefined : Notification,
    serviceWorker: hatPush ? (navigator.serviceWorker as unknown as PushUmgebung["serviceWorker"]) : undefined,
  };
}

export function urlBase64ZuBytes(b64: string): Uint8Array {
  const rein = b64.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(b64.length / 4) * 4, "=");
  return Uint8Array.from(atob(rein), (z) => z.charCodeAt(0));
}

/**
 * FRIST für `serviceWorker.ready` und `pushManager.getSubscription()` (Befund 3, Task 13): Beide
 * dürfen nie ohne Zeitgrenze abgewartet werden. `ready` erfüllt sich nie, solange kein Service
 * Worker aktiv ist, und `getSubscription()` hing im Prüfstand-WebKit ohne Push-Dienst unbegrenzt —
 * die Einstellungsseite blieb dann stehen. Nach 3 s gilt: kein Abo erkennbar.
 */
export const PUSH_FRIST_MS = 3000;

export class PushFristAbgelaufen extends Error {
  constructor() { super("Der Push-Dienst des Browsers antwortet nicht."); this.name = "PushFristAbgelaufen"; }
}

export function mitFrist<T>(versprechen: Promise<T>, ms = PUSH_FRIST_MS): Promise<T> {
  let uhr: ReturnType<typeof setTimeout> | undefined;
  const frist = new Promise<never>((_, ablehnen) => { uhr = setTimeout(() => ablehnen(new PushFristAbgelaufen()), ms); });
  return Promise.race([versprechen, frist]).finally(() => clearTimeout(uhr));
}

type PushManagerAusschnitt = Awaited<NonNullable<PushUmgebung["serviceWorker"]>["ready"]>["pushManager"];

/** `ready` und das bestehende Abo, beides unter EINER Frist; wirft PushFristAbgelaufen. */
async function aboMitFrist(sw: NonNullable<PushUmgebung["serviceWorker"]>):
  Promise<{ pushManager: PushManagerAusschnitt; abo: AboAusschnitt | null }> {
  return mitFrist((async () => {
    const { pushManager } = await sw.ready;
    return { pushManager, abo: await pushManager.getSubscription() };
  })());
}

/**
 * Das bestehende Abo dieses Browsers oder `null` — ohne Push-Unterstützung, ohne Abo, nach
 * abgelaufener Frist oder bei jedem Fehler. Für das Abmelden (das Gerät beim Dienst lösen,
 * ohne das Abo im Browser zu kündigen) — wirft nie.
 */
export async function aktuellesAbo(u: PushUmgebung): Promise<AboAusschnitt | null> {
  if (u.serviceWorker === undefined) return null;
  try { return (await aboMitFrist(u.serviceWorker)).abo; } catch { return null; }
}

/**
 * VAPID-SCHLÜSSELWECHSEL: Ein Abo gehört zu dem Schlüssel, mit dem es angelegt wurde; nach einem
 * Wechsel am Server nimmt der Push-Dienst die Zustellung nicht mehr an. Unbekannt (Browser ohne
 * `options`) gilt als passend — sonst würde dort bei jedem Abstimmen neu abonniert.
 */
function andererSchluessel(abo: AboAusschnitt, publicKey: string): boolean {
  const alt = abo.options?.applicationServerKey;
  if (alt === undefined || alt === null) return false;
  const a = new Uint8Array(alt);
  const b = urlBase64ZuBytes(publicKey);
  return a.length !== b.length || a.some((x, i) => x !== b[i]);
}

/** Das alte Abo kündigen (Browser) und beim Dienst lösen — beides bestmöglich. */
async function altesAboVerwerfen(
  client: Partial<Pick<BenachrichtigungsEinstellungenClient, "unregisterPush">>, abo: AboAusschnitt,
): Promise<void> {
  try { await abo.unsubscribe(); } catch { /* das neue Abo ersetzt es ohnehin */ }
  try { await client.unregisterPush?.(abo.endpoint); } catch { /* der Zusteller legt es bei 404/410 still */ }
}

export async function geraeteZustand(u: PushUmgebung): Promise<{ zustand: GeraeteZustand; endpoint: string | null }> {
  if (u.serviceWorker === undefined || u.notification === undefined) return { zustand: "NICHT_UNTERSTUETZT", endpoint: null };
  if (u.notification.permission === "denied") return { zustand: "NICHT_ERLAUBT", endpoint: null };
  let abo: AboAusschnitt | null;
  try { ({ abo } = await aboMitFrist(u.serviceWorker)); } catch (f) {
    if (!(f instanceof PushFristAbgelaufen)) throw f;
    // Ohne steuernden Service Worker kann dieses Fenster keinen Push empfangen.
    const gesteuert = u.serviceWorker.controller !== undefined && u.serviceWorker.controller !== null;
    return { zustand: gesteuert ? "AUS" : "NICHT_UNTERSTUETZT", endpoint: null };
  }
  return abo === null ? { zustand: "AUS", endpoint: null } : { zustand: "AN", endpoint: abo.endpoint };
}

export async function pushEinschalten(
  client: Pick<BenachrichtigungsEinstellungenClient, "getPushKey" | "registerPush">
    & Partial<Pick<BenachrichtigungsEinstellungenClient, "unregisterPush">>, u: PushUmgebung,
): Promise<GeraeteZustand> {
  if (u.serviceWorker === undefined || u.notification === undefined) return "NICHT_UNTERSTUETZT";
  const erlaubnis = u.notification.permission === "granted" ? "granted" : await u.notification.requestPermission();
  // Weggeklickt ("default") ist keine Sperre: Der Nutzer kann es später noch einmal versuchen.
  if (erlaubnis === "denied") return "NICHT_ERLAUBT";
  if (erlaubnis !== "granted") return "AUS";
  const { publicKey } = await client.getPushKey();
  // Frist nur für `ready` und das Nachsehen; das Abonnieren selbst spricht mit dem Push-Dienst
  // und darf dauern. Läuft die Frist ab, meldet die Seite „ließ sich nicht umschalten".
  const { pushManager, abo: vorhanden } = await aboMitFrist(u.serviceWorker);
  let bestehend = vorhanden;
  if (bestehend !== null && andererSchluessel(bestehend, publicKey)) {
    await altesAboVerwerfen(client, bestehend);
    bestehend = null;
  }
  const abo = bestehend
    ?? await pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ZuBytes(publicKey) });
  await client.registerPush(abo.toJSON() as Parameters<typeof client.registerPush>[0]);
  return "AN";
}

/**
 * ABSTIMMEN: Ein schon bestehendes Browser-Abo noch einmal beim Dienst anmelden (Upsert per
 * Endpunkt, idempotent). Das überträgt das Gerät auf den gerade angemeldeten Nutzer (F105:
 * Nutzerwechsel am selben Gerät), heilt ein anderswo entferntes oder nach einem gescheiterten
 * `pushsubscriptionchange` verwaistes Gerät und liefert die Kennung „dieses Geräts". Fragt nie
 * nach einer Erlaubnis — ohne Erlaubnis oder Abo: `null`. Einzige Ausnahme vom „nie ein neues
 * Abo": Gehört das bestehende zu einem anderen VAPID-Schlüssel als dem des Servers, wird es
 * gekündigt und still neu abonniert (die Erlaubnis liegt ja vor; ohne Handlung möglich).
 * Ist der Schlüssel nicht abrufbar, wird wie bisher nur angemeldet.
 */
export async function pushAbstimmen(
  client: Pick<BenachrichtigungsEinstellungenClient, "registerPush" | "getPushKey">
    & Partial<Pick<BenachrichtigungsEinstellungenClient, "unregisterPush">>, u: PushUmgebung,
): Promise<PushGeraet | null> {
  if (u.serviceWorker === undefined || u.notification === undefined) return null;
  if (u.notification.permission !== "granted") return null;
  let abo: AboAusschnitt | null;
  let pushManager: PushManagerAusschnitt;
  try { ({ abo, pushManager } = await aboMitFrist(u.serviceWorker)); } catch (f) {
    if (f instanceof PushFristAbgelaufen) return null;
    throw f;
  }
  if (abo === null) return null;
  let publicKey: string | null = null;
  try { ({ publicKey } = await client.getPushKey()); } catch { /* ohne Schlüssel kein Vergleich */ }
  if (publicKey !== null && andererSchluessel(abo, publicKey)) {
    await altesAboVerwerfen(client, abo);
    abo = await pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ZuBytes(publicKey) });
  }
  return client.registerPush(abo.toJSON() as Parameters<typeof client.registerPush>[0]);
}

export async function pushAusschalten(
  client: Pick<BenachrichtigungsEinstellungenClient, "unregisterPush">, u: PushUmgebung,
): Promise<void> {
  if (u.serviceWorker === undefined) return;
  const { abo } = await aboMitFrist(u.serviceWorker);
  if (abo === null) return;
  await client.unregisterPush(abo.endpoint);
  await abo.unsubscribe();
}

/** „Safari auf iPhone", „Chrome auf Windows" — grob aus dem User-Agent, nur zum Wiedererkennen. */
export function geraeteName(userAgent: string | null): string {
  if (userAgent === null) return "unbekanntes Gerät";
  const browser = /Edg\//.test(userAgent) ? "Edge" : /Firefox\//.test(userAgent) ? "Firefox"
    : /Chrome\//.test(userAgent) ? "Chrome" : /Safari/.test(userAgent) ? "Safari" : "Browser";
  const system = /iPhone/.test(userAgent) ? "iPhone" : /iPad/.test(userAgent) ? "iPad" : /Android/.test(userAgent) ? "Android"
    : /Mac OS X|Macintosh/.test(userAgent) ? "Mac" : /Windows/.test(userAgent) ? "Windows" : /Linux/.test(userAgent) ? "Linux" : "";
  return system === "" ? browser : `${browser} auf ${system}`;
}
